import { useEffect, useState } from 'react'
import { routeStores, type Store, type TripWithItems } from '@sp/shared'
import { api } from '../lib/api.ts'
import { inStore, plural, storeShortName } from '../lib/format.ts'
import { useTrips } from '../lib/groupData.ts'
import { HttpError } from '../lib/http.ts'
import { saveCachedTrip } from '../lib/outbox.ts'
import { errorMessage, invalidateResources } from '../lib/useResource.ts'
import { Icon } from './Icon.tsx'
import { Button, Chip, Label, Paragraph, TextField } from './ui.tsx'
import { RouteEditor } from './RouteEditor.tsx'

/** Od tolika obchodu se misto cipu ukaze editor s mesty a hledanim. */
const CHIPS_UP_TO = 8

/** Dnesni datum v mistnim case (sv-SE je tvar 2026-09-19). */
export function todayIso(): string {
  return new Date().toLocaleDateString('sv-SE')
}

/**
 * Zalozeni nakupu jednou otazkou „Kam jdeš?" (navrh v3). Druh nakupu se neresi:
 * jeden obchod nebo kdekoli = rychly seznam, vic obchodu = trasa. Datum a nazev
 * jsou nepovinne.
 */
export function TripStart({
  groupId,
  stores,
  defaultTitle = '',
  submitLabel,
  onAddStore,
  onCreated,
  addedStoreId = null,
}: {
  groupId: string
  stores: readonly Store[]
  defaultTitle?: string | undefined
  /** Vlastni text tlacitka (pocet obchodu se doplni sam). */
  submitLabel?: ((count: number) => string) | undefined
  /** „Jiný obchod" — rodic ukaze formular obchodu. */
  onAddStore?: (() => void) | undefined
  onCreated(trip: TripWithItems): void
  /** Obchod prave zalozeny pres „Jiný obchod" — rovnou se vybere. */
  addedStoreId?: string | null | undefined
}) {
  const trips = useTrips(groupId)
  const route = routeStores(stores)
  const [selected, setSelected] = useState<string[]>([])
  // Bez data plati dnesni akce; datum zalozeni by u seznamu pouziteho za tri dny
  // hledalo akce, ktere uz skoncily.
  const [date, setDate] = useState('')
  const [title, setTitle] = useState(defaultTitle)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (addedStoreId) setSelected((prev) => (prev.includes(addedStoreId) ? prev : [...prev, addedStoreId]))
  }, [addedStoreId])

  // „Jako minule": obchody posledniho nakupu s vic obchody, jen ty, ktere ve skupine jeste jsou.
  const lastRoute = ((trips.res.state === 'ok' ? trips.res.data : [])
    .filter((t) => t.storeIds.length > 1)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]?.storeIds ?? [])
    .flatMap((id) => route.find((s) => s.id === id) ?? [])
  const sameAsLast = lastRoute.length > 0 && lastRoute.map((s) => s.id).join() === selected.join()

  // Vybrane obchody v poradi obchodu skupiny (vychozi trasa).
  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : route.map((s) => s.id).filter((x) => x === id || prev.includes(x)),
    )

  async function create() {
    setBusy(true)
    setError(null)
    try {
      const trip = await api.trips.create(groupId, {
        kind: selected.length > 1 ? 'big' : 'quick',
        title: title.trim() || null,
        plannedDate: date || null,
        storeIds: selected,
      })
      // Obrazovka nakupu se otevre hned z cache, bez dalsiho nacitani.
      saveCachedTrip(trip)
      invalidateResources(`sp:nakupy:${groupId}`)
      onCreated(trip)
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Nový nákup jde založit jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const first = route.find((s) => s.id === selected[0])
  const label = submitLabel
    ? submitLabel(selected.length)
    : selected.length === 0
      ? 'Začít nákup kdekoli'
      : selected.length === 1 && first
        ? `Začít nákup ${inStore(first.name)}`
        : `Začít nákup · ${plural(selected.length, 'obchod', 'obchody', 'obchodů')}`

  return (
    <div className="stack">
      <div className="stack-8">
        <Label>Kam jdeš?</Label>
        {route.length <= CHIPS_UP_TO ? (
          <div className="chips">
            <Chip active={selected.length === 0} onClick={() => setSelected([])}>
              Kdekoli
            </Chip>
            {route.map((store) => (
              <Chip key={store.id} active={selected.includes(store.id)} onClick={() => toggle(store.id)}>
                {selected.includes(store.id) && <Icon name="check" size={16} />}
                {store.name}
              </Chip>
            ))}
            {onAddStore && (
              <Chip plus onClick={onAddStore}>
                Jiný obchod
              </Chip>
            )}
          </div>
        ) : (
          <>
            <RouteEditor stores={stores} selected={selected} onChange={setSelected} />
            {onAddStore && (
              <Button kind="text" icon="plus" compact className="self-start" onClick={onAddStore}>
                Jiný obchod
              </Button>
            )}
          </>
        )}
        {lastRoute.length > 1 && !sameAsLast && (
          <Chip className="chip-long self-start" onClick={() => setSelected(lastRoute.map((s) => s.id))}>
            <Icon name="refresh" size={16} />
            {`Jako minule: ${lastRoute.map((s) => storeShortName(s.name)).join(' → ')}`}
          </Chip>
        )}
        {selected.length > 1 && route.length <= CHIPS_UP_TO && (
          <Paragraph size="meta">
            {`Trasa: ${selected.flatMap((id) => route.find((s) => s.id === id)?.name ?? []).join(' → ')}. Pořadí změníš v nastavení nákupu.`}
          </Paragraph>
        )}
      </div>
      <TextField
        label="Kdy"
        type="date"
        hint="Nepovinné. S datem se slevy hledají na ten den, bez něj na dnešek."
        value={date}
        min={todayIso()}
        onChange={(e) => setDate(e.target.value)}
      />
      <TextField
        label="Název"
        placeholder="Nepovinné, třeba Grilovačka u Pepy"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={80}
      />
      {error && <Paragraph tone="error">{error}</Paragraph>}
      <Button kind="primary" full disabled={busy} onClick={() => void create()}>
        {label}
      </Button>
    </div>
  )
}
